import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import firebase from '../db/firebase/firebase';
import { v4 as uuidv4 } from 'uuid';
import { ArrowLeft, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { motion } from 'framer-motion';

const RequestMoneyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [action, setAction] = useState(null);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const docRef = doc(firebase.db, 'requestsMoney', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setRequest({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error fetching request:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRequest();
  }, [id]);

  const getStateStyle = (state) => {
    switch (state) {
      case 'approved':
        return 'text-green-600 font-bold';
      case 'pending':
        return 'text-yellow-600 font-bold';
      case 'rejected':
        return 'text-red-600 font-bold';
      default:
        return 'text-gray-800';
    }
  };

  const confirmApprove = async () => {
    const confirmed = window.confirm('Are you sure you want to approve this request? This action cannot be undone.');
    if (!confirmed) return;

    try {
      let receiptUrl = '';
      if (receiptFile) {
        const storagePath = `receipts/${request.id}-${uuidv4()}`;
        const storageRef = ref(firebase.storage, storagePath);
        await uploadBytes(storageRef, receiptFile);
        receiptUrl = await getDownloadURL(storageRef);
      }

      await updateDoc(doc(firebase.db, 'requestsMoney', request.id), {
        state: 'approved',
        receiptUrl: receiptUrl || '',
      });

      alert('Request approved successfully');
      navigate(-1);
    } catch (err) {
      console.error('Error approving:', err);
      alert('There was an error approving the request.');
    }
  };

  const confirmReject = async () => {
    const confirmed = window.confirm('Are you sure you want to reject this request? This action cannot be undone. Points will be returned to the user.');
    if (!confirmed) return;

    if (!rejectionNote.trim()) {
      alert('Please enter a rejection message.');
      return;
    }

    const userEmail = request.user?.email;
    const amount = request.amount;

    if (!userEmail || typeof amount !== 'number') {
      alert('Missing user email or amount.');
      return;
    }

    try {
      await updateDoc(doc(firebase.db, 'requestsMoney', request.id), {
        state: 'rejected',
        rejectionNote: rejectionNote.trim(),
      });

      await updateDoc(doc(firebase.db, 'userData', userEmail), {
        points: increment(amount),
      });

      alert('Request rejected and points returned.');
      navigate(-1);
    } catch (err) {
      console.error('Error rejecting request:', err);
      alert('There was an error rejecting the request.');
    }
  };

  const isPDF = receiptFile && receiptFile.type === 'application/pdf';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
      </div>
    );
  }

  if (!request) {
    return <p className="p-6 text-center text-red-600">Request not found.</p>;
  }

  return (
    <motion.div
      className="min-h-screen flex flex-col bg-gray-100 p-6 flex-1 overflow-y-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-blue-600 hover:underline mb-4"
      >
        <ArrowLeft className="mr-2" /> Back
      </button>

      <div className="bg-white shadow rounded-lg p-6 space-y-6 max-w-5xl mx-auto w-full flex-1">
        <h2 className="text-2xl font-bold">Money Request Details</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b pb-4">
          <div>
            <h3 className="font-semibold text-gray-600 sticky top-0 bg-white py-1 z-10">User Information</h3>
            <p><strong>Name:</strong> {request.accountHolder}</p>
            <p><strong>Email:</strong> {request.user?.email || '-'}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-600 sticky top-0 bg-white py-1 z-10">Bank Details</h3>
            <p><strong>Bank Name:</strong> {request.bankName}</p>
            <p><strong>Account Number:</strong> {request.accountNumber}</p>
            <p><strong>Sort Code:</strong> {request.sortCode}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b pb-4">
          <div>
            <h3 className="font-semibold text-gray-600 sticky top-0 bg-white py-1 z-10">Transaction</h3>
            <p className="text-xl font-bold text-green-600"><strong>Amount:</strong> £{request.amount}</p>
            <p><strong>Points Before:</strong> {request.pointsBefore}</p>
            <p><strong>Points After:</strong> {request.pointsAfter}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-600 sticky top-0 bg-white py-1 z-10">Status & Dates</h3>
            <p><strong>Status:</strong> <span className={getStateStyle(request.state)}>{request.state}</span></p>
            <p><strong>Created At:</strong> {new Date(request.createdAt).toLocaleString()}</p>
            {request.receiptUrl && (
              <p><strong>Receipt:</strong> <a href={request.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View</a></p>
            )}
            {request.rejectionNote && (
              <p><strong>Rejection Note:</strong> {request.rejectionNote}</p>
            )}
          </div>
        </div>

        {request.state === 'pending' && (
          <div className="space-y-4 pt-2">
            <div className="flex gap-4">
              <button
                onClick={() => setAction('approve')}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                <CheckCircle2 size={18} /> Approve
              </button>
              <button
                onClick={() => setAction('reject')}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                <XCircle size={18} /> Reject
              </button>
            </div>

            {action === 'approve' && (
              <div className="mt-4">
                <label className="block font-medium mb-1">Upload Receipt:</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    setReceiptFile(e.target.files[0]);
                    setReceiptPreview(URL.createObjectURL(e.target.files[0]));
                  }}
                  className="mb-2"
                />
                {receiptPreview && (
                  <div className="mb-4">
                    <p className="font-medium">Preview:</p>
                    {isPDF ? (
                      <div className="flex items-center text-gray-600 gap-2">
                        <FileText size={20} /> <span>{receiptFile.name}</span>
                      </div>
                    ) : (
                      <img src={receiptPreview} alt="Receipt Preview" className="mt-2 max-h-64 rounded" />
                    )}
                  </div>
                )}
                <button
                  onClick={confirmApprove}
                  className="mt-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Confirm Approval
                </button>
              </div>
            )}

            {action === 'reject' && (
              <div className="mt-4">
                <label className="block font-medium mb-1">Rejection message:</label>
                <textarea
                  rows={4}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Explain the reason for rejection"
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                />
                <button
                  onClick={confirmReject}
                  className="mt-3 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Confirm Rejection
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RequestMoneyDetail;